<template lang="html">
  <d-form @submit.prevent="saveInstallments">
    <d-card class="card-small">
      <d-card-header class="border-bottom">
        <h6 class="m-0">Parcelamento a prazo</h6>
      </d-card-header>

      <d-card-body>
        <d-form-row>
          <d-col cols="12" class="form-group">
            <label for="quotationType">Tipo de orçamento</label>
            <d-select
              id="quotationType"
              v-model="selectedTypeId"
            >
              <option
                v-for="option in quotationTypes"
                :value="option.id"
                :key="option.id"
              >
                {{ option.name }}
              </option>
            </d-select>
          </d-col>
        </d-form-row>

        <template v-if="selectedTypeId">
          <d-form-row
            class="justify-content-end"
            v-for="(payment, paymentIndex) in installments"
            :key="paymentIndex"
          >
            <d-col md="7" cols="12" class="form-group mb-0">
              <label>Entrada</label>
              <d-input-group class="mb-2 discount">
                <d-input-group-addon prepend>
                  <select
                    class="input-group-text form-control custom-select"
                    v-model="payment.entree_type"
                  >
                    <option value="pct" selected>%</option>
                    <option value="value">R$</option>
                  </select>
                </d-input-group-addon>
                <input-currency
                  model-index="entree_value"
                  placeholder="Entrada"
                  hiddenGroup=""
                  :parameter="paymentIndex"
                  :model="payment"
                />
              </d-input-group>
            </d-col>

            <d-col md="3" cols="12" class="form-group mb-0">
              <label>Parcelas</label>
              <d-form-input
                type="number"
                class="text-right"
                min="1"
                v-model="payment.parcel"
              />
            </d-col>

            <d-col md="2" cols="2">
              <div class="d-flex justify-content-end align-items-center h-100 pt-4">
                <i
                  class="fas fa-plus text-success mr-2"
                  @click="addInstallment()"
                  v-if="showAddNewInstallment(paymentIndex)"
                ></i>
                <i
                  class="fas fa-minus text-danger"
                  @click="removeInstallment(paymentIndex)"
                  v-if="installments.length > 1"
                ></i>
              </div>
            </d-col>
          </d-form-row>
        </template>
      </d-card-body>

      <d-card-footer class="border-top">
        <d-row>
          <d-col>
            <d-button
              type="submit"
              class="btn-primary"
              :disabled="loading || !selectedTypeId"
            >
              Salvar
            </d-button>
          </d-col>
        </d-row>
      </d-card-footer>
    </d-card>
  </d-form>
</template>

<script>
import { mapActions } from 'vuex';
import { cloneDeep } from '_utils';
import InputCurrency from '_shared/InputCurrency.vue';
import QuotationService from '_services/rest/modules/quotation';
import { PAYMENT } from '../../quotation/components/QuotationModel';
import {
  buildForwardInstallments,
  parseApiList,
} from '../../quotation/components/installmentsPlan';

export default {
  name: 'quotation-type-installments-form',
  components: {
    InputCurrency,
  },
  data() {
    return {
      loading: false,
      selectedTypeId: null,
      quotationTypes: [],
      installments: [],
    };
  },
  watch: {
    selectedTypeId() {
      this.loadInstallments();
    },
  },
  methods: {
    ...mapActions([
      'activeAlert',
    ]),
    syncTypeOptionsStore() {
      this.$store.commit('setTypeOptions', cloneDeep(this.quotationTypes));
    },
    getQuotationTypes() {
      QuotationService.getTypeOptions()
        .then(response => {
          this.quotationTypes = parseApiList(response).filter(type => type.id);
          this.syncTypeOptionsStore();
        })
        .catch(error => {
          this.activeAlert({
            message: `${error.message} <br/> ${error.data}`,
            theme: 'danger',
          });
        });
    },
    getSelectedType() {
      return this.quotationTypes.find(type => String(type.id) === String(this.selectedTypeId));
    },
    parseSavedInstallments(response) {
      if (response && Array.isArray(response.installments)) {
        return response.installments;
      }
      if (response && response.data && Array.isArray(response.data.installments)) {
        return response.data.installments;
      }
      if (Array.isArray(response)) {
        return response;
      }
      return null;
    },
    mapInstallments(installments) {
      return buildForwardInstallments(installments);
    },
    loadInstallments() {
      const selectedType = this.getSelectedType();
      this.installments = this.mapInstallments(selectedType && selectedType.installments);
    },
    addInstallment() {
      if (this.installments.length < 3) {
        this.installments.push(cloneDeep({
          ...PAYMENT,
          entree_value: 0,
          parcel: 1,
          payment_type: 'forward',
        }));
      }
    },
    removeInstallment(paymentIndex) {
      this.installments.splice(paymentIndex, 1);
    },
    showAddNewInstallment(paymentIndex) {
      return (
        this.installments.length < 3
        && paymentIndex + 1 === this.installments.length
      );
    },
    prepareInstallmentsPayload() {
      return this.installments.map(installment => {
        const payload = {
          entree_type: installment.entree_type,
          entree_value: installment.entree_value,
          parcel: installment.parcel,
          payment_type: 'forward',
        };
        if (installment.id) {
          payload.id = installment.id;
        }
        return payload;
      });
    },
    saveInstallments() {
      const selectedType = this.getSelectedType();

      if (!selectedType) {
        return;
      }

      this.loading = true;
      QuotationService.saveQuotationTypeInstallments(
        selectedType.id,
        { installments: this.prepareInstallmentsPayload() },
      )
        .then(response => {
          const savedInstallments = this.parseSavedInstallments(response);

          selectedType.installments = savedInstallments
            || this.prepareInstallmentsPayload();

          this.installments = this.mapInstallments(selectedType.installments);
          this.syncTypeOptionsStore();
          this.activeAlert({
            message: 'Formas de pagamento salvas com sucesso',
            theme: 'success',
          });
        })
        .catch(error => {
          this.activeAlert({
            message: `${error.message} <br/> ${error.data}`,
            theme: 'danger',
          });
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
  mounted() {
    this.getQuotationTypes();
  },
};
</script>

<style lang="scss" scoped>
.border-top {
  border-color: #3D5170;
}
</style>
